import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import {
    useForgotPassword,
    useResendOtp,
    useResetPassword
} from "@/features/auth/repositories/auth-repository";
import {
    ForgotPasswordSchema,
    ResetPasswordSchema,
    TForgotPasswordSchema,
    TResetPasswordSchema
} from "@/features/auth/forms/schema";

import Button from "@/components/buttons/Button";
import TextInput from "@/components/form-controls/TextInput"


// The reset is a two-step flow against the API: request an OTP for an email,
// then submit that OTP together with the new password. The email is held here
// between the steps so the user only types it once.
const ForgotPasswordForm = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState<string>("");

    const forgotMutation = useForgotPassword(
        () => setEmail(requestForm.getValues("username"))
    );

    const resendMutation = useResendOtp();

    const resetMutation = useResetPassword(
        () => navigate("/auth/login", { replace: true })
    );

    const requestForm = useForm({
        resolver: yupResolver(ForgotPasswordSchema),
        defaultValues: { username: "" }
    });

    const resetForm = useForm({
        resolver: yupResolver(ResetPasswordSchema),
        defaultValues: {
            username: "",
            otp: "",
            password: "",
            repeatPassword: ""
        }
    });

    const submitRequest = (data: TForgotPasswordSchema) => {
        forgotMutation.mutate(data.username);
    }

    const submitReset = (data: TResetPasswordSchema) => {
        resetMutation.mutate({ ...data, username: email });
    }

    if (!email) {
        return (
            <form
                className="space-y-8"
                onSubmit={requestForm.handleSubmit(submitRequest)}
            >
                <TextInput
                    label="Email"
                    placeholder="example@gmail.com"
                    hasError={requestForm.formState.errors.username?.type != undefined}
                    error={requestForm.formState.errors.username?.message?.toString()}
                    register={requestForm.register("username")}
                />

                <Button
                    type="submit"
                    intent={"primary"}
                    loading={forgotMutation.isPending}
                    size={"md"}
                    className="w-full"
                >
                    Send Reset Code
                </Button>

                <div className="text-sm text-secondary">
                    <span>Remembered your password?</span>
                    <Link
                        to="/auth/login"
                        className="text-primary hover:underline pl-2"
                    >
                        Back to login
                    </Link>
                </div>
            </form>
        )
    }

    return (
        <form
            className="space-y-8"
            onSubmit={resetForm.handleSubmit(submitReset)}
        >
            <p className="text-sm text-secondary font-light">
                We sent a reset code to <span className="font-medium">{email}</span>.
            </p>

            <div className="space-y-4">
                <TextInput
                    label="Reset Code"
                    placeholder="enter the code from your email"
                    hasError={resetForm.formState.errors.otp?.type != undefined}
                    error={resetForm.formState.errors.otp?.message?.toString()}
                    register={resetForm.register("otp")}
                />

                <TextInput
                    label="New Password"
                    placeholder="new password"
                    type="password"
                    hasError={resetForm.formState.errors.password?.type != undefined}
                    error={resetForm.formState.errors.password?.message?.toString()}
                    register={resetForm.register("password")}
                />

                <TextInput
                    label="Confirm New Password"
                    placeholder="repeat new password"
                    type="password"
                    hasError={resetForm.formState.errors.repeatPassword?.type != undefined}
                    error={resetForm.formState.errors.repeatPassword?.message?.toString()}
                    register={resetForm.register("repeatPassword")}
                />
            </div>

            <Button
                type="submit"
                intent={"primary"}
                loading={resetMutation.isPending}
                size={"md"}
                className="w-full"
            >
                Reset Password
            </Button>

            <div className="flex justify-between text-sm text-secondary">
                <button
                    type="button"
                    onClick={() => resendMutation.mutate(email)}
                    disabled={resendMutation.isPending}
                    className="text-primary hover:underline disabled:opacity-50"
                >
                    Resend code
                </button>

                <button
                    type="button"
                    onClick={() => setEmail("")}
                    className="text-primary hover:underline"
                >
                    Use a different email
                </button>
            </div>
        </form>
    )
}

export default ForgotPasswordForm
